import { MeshBackground } from '@denqfabrik/landing-ui';
import { useEffect, useState } from 'react';
import { useTranslation } from '../../i18n';
import { getRoutesForLang } from '../../i18n/routes';

// ── Claude Code simulation lines ────────────────────────────────────────────

interface TermLine {
  type: 'user' | 'agent' | 'tool-call' | 'pass' | 'fail' | 'blank';
  text: string;
}

// ── Claude Code mascot ──────────────────────────────────────────────────────

function ClaudeMascot({ className = '' }: { className?: string }) {
  return (
    <svg viewBox="0 0 32 28" className={className} aria-hidden="true">
      <rect x="8" y="4" width="16" height="12" rx="2" fill="#D4634A" />
      <rect x="12" y="8" width="3" height="4" rx="0.5" fill="#1a1a2e" />
      <rect x="18" y="8" width="3" height="4" rx="0.5" fill="#1a1a2e" />
      <rect x="9" y="16" width="3" height="6" fill="#D4634A" />
      <rect x="14" y="16" width="3" height="6" fill="#D4634A" />
      <rect x="20" y="16" width="3" height="6" fill="#D4634A" />
      <rect x="11" y="0" width="2" height="5" fill="#D4634A" />
      <rect x="19" y="0" width="2" height="5" fill="#D4634A" />
    </svg>
  );
}

// ── Component ───────────────────────────────────────────────────────────────

export default function Hero() {
  const { t, lang } = useTranslation();
  const lines = t.hero.demoLines as TermLine[];
  const [visibleCount, setVisibleCount] = useState(0);

  useEffect(() => {
    if (visibleCount >= lines.length) return;
    const line = lines[visibleCount];
    const delay = line.type === 'blank' ? 150 : 450 + Math.random() * 250;
    const timer = setTimeout(() => setVisibleCount((c) => c + 1), delay);
    return () => clearTimeout(timer);
  }, [visibleCount, lines.length]);

  const routes = getRoutesForLang(lang);
  const howItWorksHref = `/${lang}${routes['how-it-works']}`;
  const earlyAccessHref = `/${lang}${routes['early-access']}`;

  return (
    <section
      id="hero"
      data-nav-theme="dark"
      className="relative isolate min-h-[100svh] overflow-x-clip pt-24 text-slate-100 sm:pt-32"
    >
      <MeshBackground mode="container" />

      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-[1]"
        style={{
          background:
            'radial-gradient(circle at 18% 12%, rgba(25,72,170,0.12), transparent 54%), radial-gradient(circle at 82% 88%, rgba(30,112,210,0.1), transparent 60%), linear-gradient(180deg, rgba(5,9,20,0.16) 0%, rgba(3,8,20,0.24) 100%)',
        }}
      />

      <div className="relative z-10 mx-auto grid w-full max-w-[1400px] grid-cols-1 gap-6 px-4 pb-10 sm:px-6 sm:pb-16 lg:min-h-[calc(100svh-132px)] lg:grid-cols-[0.46fr_0.54fr] lg:items-center lg:gap-10 lg:px-8 lg:pb-12 xl:gap-12">
        {/* ── Left — copy ────────────────────────────────────── */}
        <div className="max-w-[420px]">
          <div className="hero-animate mb-5">
            <div className="inline-flex items-center gap-2 rounded-full border border-sky-300/20 bg-slate-900/40 px-3 py-1.5 text-xs text-sky-100 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.03)] backdrop-blur-sm">
              <span className="h-2 w-2 rounded-full bg-sky-300 shadow-[0_0_12px_rgba(56,189,248,0.9)]" />
              <span>{t.hero.badge}</span>
            </div>
          </div>

          <h1
            className="hero-animate font-display text-[2.2rem] font-semibold leading-[1.1] tracking-tight sm:text-5xl lg:text-[3.1rem]"
            style={{ animationDelay: '120ms' }}
          >
            <span className="text-slate-100">{t.hero.headlineLine1} </span>
            <span className="bg-gradient-to-r from-sky-400 via-cyan-300 to-blue-200 bg-clip-text text-transparent">
              {t.hero.headlineLine2}
            </span>
          </h1>

          <p
            className="hero-animate mt-4 max-w-[36ch] text-[0.95rem] leading-relaxed text-slate-300/90 sm:text-base"
            style={{ animationDelay: '220ms' }}
          >
            {t.hero.subheadline}
          </p>

          <div
            className="hero-animate mt-6 flex flex-col gap-3 sm:flex-row"
            style={{ animationDelay: '300ms' }}
          >
            <a
              href={earlyAccessHref}
              className="inline-flex items-center justify-center rounded-2xl bg-sky-100 px-5 py-3 text-sm font-medium text-slate-900 transition-all duration-300 hover:bg-white sm:px-6 sm:py-3.5 sm:text-base"
            >
              {t.hero.ctaPrimary}
            </a>
            <a
              href={howItWorksHref}
              className="inline-flex items-center justify-center rounded-2xl border border-slate-400/30 px-5 py-3 text-sm font-medium text-slate-200 transition-all duration-300 hover:border-slate-300/50 hover:text-white sm:px-6 sm:py-3.5 sm:text-base"
            >
              {t.hero.ctaSecondary}
            </a>
          </div>
        </div>

        {/* ── Right — Claude Code terminal ────────────────────── */}
        <div
          className="hero-animate w-full lg:justify-self-end"
          style={{ animationDelay: '220ms' }}
        >
          <div className="overflow-hidden rounded-xl border border-slate-600/30 shadow-[0_24px_80px_rgba(0,0,0,0.5)] sm:rounded-2xl">
            {/* Terminal chrome */}
            <div className="flex items-center gap-1.5 bg-[#2b2b2b] px-3 py-2 sm:px-4 sm:py-2.5">
              <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />
              <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" />
              <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
              <span className="ml-3 text-[11px] text-[#999]">claude — ~/my-project</span>
            </div>

            {/* Terminal body */}
            <div className="bg-[#1a1a2e] p-3 font-mono sm:p-4 lg:p-5">
              {/* Claude Code header */}
              <div className="mb-3 flex items-start gap-3">
                <ClaudeMascot className="h-9 w-7 shrink-0 sm:h-12 sm:w-9" />
                <div className="min-w-0">
                  <p className="text-xs font-bold text-[#e0e0e0] sm:text-sm">
                    Claude Code{' '}
                    <span className="font-normal text-[#666]">v2.1.98</span>
                  </p>
                  <p className="text-[10px] text-[#888] sm:text-[11px]">
                    Opus 4.6 (1M context) · ~/my-project
                  </p>
                </div>
              </div>

              <div className="mb-3 border-t border-[#333]" />

              {/* Chat lines */}
              <div className="space-y-0">
                {lines.map((line, i) => {
                  const visible = i < visibleCount;
                  return (
                    <div
                      key={i}
                      className="transition-all duration-300"
                      style={{
                        opacity: visible ? 1 : 0,
                        transform: visible ? 'none' : 'translateY(3px)',
                      }}
                    >
                      {line.type === 'blank' ? (
                        <div className="h-2 sm:h-3" />
                      ) : line.type === 'user' ? (
                        <p className="text-[11px] leading-relaxed sm:text-xs lg:text-[13px]">
                          <span className="font-bold text-[#b8a2d0]">❯ </span>
                          <span className="text-[#e0e0e0]">{line.text}</span>
                        </p>
                      ) : line.type === 'agent' ? (
                        <p className="text-[11px] leading-relaxed text-[#c8d0e0] sm:text-xs lg:text-[13px]">
                          {line.text}
                        </p>
                      ) : line.type === 'tool-call' ? (
                        <div className="my-0.5 inline-block rounded-md bg-[#252540] px-2 py-1 sm:px-2.5 sm:py-1.5">
                          <p className="text-[9px] text-[#666] sm:text-[10px]">Tool call</p>
                          <p className="text-[11px] text-[#7dd3a8] sm:text-xs lg:text-[13px]">
                            $ {line.text}
                          </p>
                        </div>
                      ) : line.type === 'pass' ? (
                        <p className="text-[11px] leading-relaxed sm:text-xs lg:text-[13px]">
                          <span className="font-bold text-[#28c840]">  ✓ </span>
                          <span className="text-[#8fbc8f]">{line.text}</span>
                        </p>
                      ) : line.type === 'fail' ? (
                        <p className="text-[11px] leading-relaxed sm:text-xs lg:text-[13px]">
                          <span className="font-bold text-[#ff5f57]">  ✗ </span>
                          <span className="text-[#e08080]">{line.text}</span>
                        </p>
                      ) : null}
                    </div>
                  );
                })}

                {/* Cursor */}
                <div className="mt-1">
                  <span className="text-[11px] font-bold text-[#b8a2d0] sm:text-xs">❯</span>
                  <span className="ml-1 inline-block h-3 w-[6px] animate-pulse bg-[#b8a2d0]/60 sm:h-3.5" />
                </div>
              </div>
            </div>

            {/* Status bar */}
            <div className="flex items-center justify-between bg-[#252540] px-3 py-1.5 text-[10px] sm:px-4 sm:text-[11px]">
              <span className="text-[#888]">
                <span className="text-[#7dd3a8]">►►</span> accept edits on
              </span>
              <span className="text-[#666]">~/my-project</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
